import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import CryptoJS from "crypto-js";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { jwtDecode } from "jwt-decode";
import safeToast from "utils/safeToast";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"; // Optional, for fallback
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import DialogContentText from "@mui/material/DialogContentText";

// @mui/material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import LogoutIcon from "@mui/icons-material/Logout";

// Custom styles
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Replace with your AES key and IV
const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = "A1B2C3D4E5F60718";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const [countdown, setCountdown] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [logoutChecked, setLogoutChecked] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // AES decryption
  const decryptText = (encryptedText) => {
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  };

  const handleLogout = async () => {
    // Disconnect SignalR connection before logout
    try {
      const signalRManager = (await import("../../services/signalRManager")).default;
      if (signalRManager && signalRManager.getConnection()) {
        await signalRManager.disconnect();
      }
    } catch (error) {
      console.error("Error disconnecting SignalR:", error);
      // Continue with logout even if SignalR disconnect fails
    }
    
    localStorage.removeItem("userSession");
    localStorage.removeItem("UserLogDetails");
    window.location.href = "/BasicAuth";
  };

  // Initial setup
  useEffect(() => {
    const sessionData = localStorage.getItem("userSession");
    const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");

    if (!sessionData || !sessionLoggedUserDetail) {
      safeToast.warn("No session found. Please login again.");
      navigate("/BasicAuth");
      return;
    }

    const decryptedUserToken = decryptText(sessionLoggedUserDetail);
    const parsedUserToken = JSON.parse(decryptedUserToken);
    const token = parsedUserToken?.Token || "";

    if (token) {
      const decoded = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const timeRemaining = decoded.exp - currentTime;

      if (timeRemaining <= 0) {
        safeToast.error("Session has expired.");
        handleLogout();
      } else {
        setCountdown(timeRemaining);
      }
    }
  }, []);

  // Countdown logic
  useEffect(() => {
    if (countdown === null) return;

    // Format time for UI
    const formatTime = (seconds) => {
      const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      return `${h}:${m}:${s}`;
    };

    setTimeLeft(formatTime(countdown));

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowSessionDialog(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [countdown]);

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");

    const handleTransparentNavbar = () => {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="No messages found" />
    </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  const handleSessionRefresh = async (e) => {
    if (e) e.preventDefault();
    setIsRefreshing(true);
    setSessionError("");
    try {
      // Get username from session/localStorage
      const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");
      const decryptedUserToken = decryptText(sessionLoggedUserDetail);
      const parsedUserToken = JSON.parse(decryptedUserToken);
      const userName = parsedUserToken?.UserName || "";

      // Encrypt username and password as in sign-in
      const encryptedUsername = CryptoJS.AES.encrypt(userName, CryptoJS.enc.Utf8.parse(AES_KEY), {
        iv: CryptoJS.enc.Utf8.parse(AES_IV),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(passwordInput, CryptoJS.enc.Utf8.parse(AES_KEY), {
        iv: CryptoJS.enc.Utf8.parse(AES_IV),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }).toString();

      // Call Auth API
      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userName: encryptedUsername,
          password: encryptedPassword,
        }),
      });

      // Debug: log status and response
      const text = await response.text();
      console.log("API status:", response.status, "API response:", text);

      if (!response.ok) throw new Error("Invalid credentials");

      // Try to decrypt and parse
      const decrypted = decryptText(text);
      console.log("Decrypted response:", decrypted);

      const parsed = JSON.parse(decrypted);
      const token = parsed?.Token || "";
      if (token) {
        const decoded = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeRemaining = decoded.exp - currentTime;
        setCountdown(timeRemaining);
        setShowSessionDialog(false);
        setPasswordInput("");
        setIsRefreshing(false);
        setSnackbarMsg("Session extended!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        localStorage.setItem("UserLogDetails", text);
        return;
      }
      throw new Error("Invalid login response");
    } catch (err) {
      setSessionError("Password incorrect or session refresh failed.");
      setSnackbarMsg("Password incorrect or session refresh failed.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsRefreshing(false);
      console.error("Session refresh error:", err);
    }
  };

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const userDetails = localStorage.getItem("UserLogDetails");
        if (!userDetails) return;
        const decryptedUser = decryptText(userDetails);
        const { StudentId, Token } = JSON.parse(decryptedUser);
        const res = await fetch(
          API_ENDPOINTS.PROFILE.GET_STUDENT_IMAGE(StudentId),
          {
            headers: { Authorization: `Bearer ${Token}` },
          }
        );
        const data = await res.json();
        const imgObj = data && data.length > 0 ? data[0] : null;
        if (imgObj) {
          if (imgObj.imageData) {
            const hex = imgObj.imageData.startsWith("0x") ? imgObj.imageData.slice(2) : imgObj.imageData;
            const byteArray = hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16));
            const uint8Array = new Uint8Array(byteArray);
            const base64String = btoa(String.fromCharCode(...uint8Array));
            setProfileImageUrl(`data:${imgObj.contentType};base64,${base64String}`);
          } else if (imgObj.fileName) {
            setProfileImageUrl(getUploadUrl(imgObj.fileName));
          } else {
            setProfileImageUrl("");
          }
        } else {
          setProfileImageUrl("");
        }
      } catch (err) {
        setProfileImageUrl("");
      }
    };

    fetchProfileImage();
  }, []);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>

        {!isMini && (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox pr={1} display="flex" alignItems="center">
              <Tooltip title="Session expires soon">
                <Chip
                  icon={
                    <AccessTimeIcon
                      sx={{
                        color:
                          countdown > 300
                            ? "#4caf50"
                            : countdown > 60
                              ? "#ff9800"
                              : "#f44336", // green, orange, red
                      }}
                    />
                  }
                  label={`⏳ ${timeLeft}`}
                  sx={{
                    color:
                      countdown > 300
                        ? "#4caf50"
                        : countdown > 60
                          ? "#ff9800"
                          : "#f44336", // Text color
                    backgroundColor: "#fff", // White background
                    border: "1.5px solid",
                    borderColor:
                      countdown > 300
                        ? "#4caf50"
                        : countdown > 60
                          ? "#ff9800"
                          : "#f44336", // Border color
                    width: "180px",
                    justifyContent: "flex-start",
                    fontWeight: "600",
                    borderRadius: "12px",
                    fontSize: "0.875rem",
                    padding: "0 10px",
                    letterSpacing: "0.5px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease-in-out",
                  }}
                />
              </Tooltip>
            </MDBox>


            <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox>

            <MDBox color={light ? "white" : "inherit"}>
              <Tooltip title="Profile">
                <Link to="/profile">
                  <IconButton sx={navbarIconButton} size="small">
                    {profileImageUrl ? (
                      <Avatar
                        src={profileImageUrl}
                        sx={{ width: 32, height: 32 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: "#bdbdbd" }}>
                        <AddAPhotoIcon fontSize="small" />
                      </Avatar>
                    )}
                  </IconButton>
                </Link>
              </Tooltip>

              <Tooltip title={miniSidenav ? "Expand Menu" : "Collapse Menu"}>
                <IconButton size="small" sx={navbarMobileMenu} onClick={handleMiniSidenav}>
                  <Icon sx={iconsStyle}>{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="Settings">
                <IconButton size="small" sx={navbarIconButton} onClick={handleConfiguratorOpen}>
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="Notifications">
                <IconButton size="small" sx={navbarIconButton} onClick={handleOpenMenu}>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="Logout">
                <IconButton size="small" sx={navbarIconButton} onClick={() => setShowLogoutConfirm(true)}>
                  <LogoutIcon sx={iconsStyle} />
                </IconButton>
              </Tooltip>

              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
      <Dialog
        open={showSessionDialog}
        onClose={() => { }}
        disableEscapeKeyDown
        fullScreen={fullScreen}
      >
        <DialogTitle>Session Expiring</DialogTitle>
        <form onSubmit={handleSessionRefresh}>
          <DialogContent>
            <Typography gutterBottom>
              Your session is ending soon. Please enter your password to continue this session.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={logoutChecked}
                  onChange={e => {
                    if (e.target.checked) {
                      setShowLogoutConfirm(true);
                    } else {
                      setLogoutChecked(false);
                    }
                  }}
                  color="secondary"
                />
              }
              label="Logout and go to login page"
              sx={{ mb: 1 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              disabled={isRefreshing || logoutChecked}
              autoFocus
              margin="dense"
              onKeyDown={e => {
                if (e.key === "Enter" && !logoutChecked) {
                  handleSessionRefresh(e);
                }
              }}
            />
            {sessionError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {sessionError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => {
                setShowSessionDialog(false);
                handleLogout();
              }}
              color="secondary"
              disabled={isRefreshing || !logoutChecked}
              variant="outlined"
              startIcon={<LogoutIcon />}
            >
              Logout
            </MDButton>
            <MDButton
              type="submit"
              color="info"
              disabled={isRefreshing || !passwordInput || logoutChecked}
              variant="gradient"
            >
              {isRefreshing ? "Refreshing..." : "Continue Session"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout? It&#39;s best to enter your password to continue the same session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowLogoutConfirm(false);
            setLogoutChecked(false);
          }} color="primary">
            Cancel
          </Button>
          <Button onClick={() => {
            setShowLogoutConfirm(false);
            setLogoutChecked(true);
            handleLogout();
          }} color="secondary" autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
